import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import api from '../services/api';
import AddTeacherModal from '../components/teachers/AddTeacherModal';

function StatusBadge({ isActive }) {
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
      isActive ? 'bg-green-100 text-green-700' : 'bg-teacher-bg-secondary text-teacher-text-primary'
    }`}>
      {isActive ? 'ACTIVE' : 'INACTIVE'}
    </span>
  );
}

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    async function fetchTeachers() {
      try {
        setLoading(true);
        const result = await api.getTeachers({ filter, search, limit: 50 });
        setTeachers(result.data.teachers);
        setTotal(result.data.total);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchTeachers();
  }, [filter, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-teacher-text-primary">Teachers</h1>
          <p className="text-teacher-text-secondary">Manage coaching class teachers and student associations</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          + Add Teacher
        </button>
      </div>

      {/* Add Teacher Modal */}
      <AddTeacherModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={async () => {
          // Refresh teachers list
          try {
            setLoading(true);
            const result = await api.getTeachers({ filter, search, limit: 50 });
            setTeachers(result.data.teachers);
            setTotal(result.data.total);
          } catch (err) {
            setError(err.message);
          } finally {
            setLoading(false);
          }
        }}
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <input
          type="text"
          placeholder="Search by name, email, or institute..."
          value={search}
          onChange={handleSearchChange}
          className="flex-1 px-4 py-2 border border-teacher-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-teacher-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="all">All Teachers</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Results count */}
      <div className="mb-4 text-sm text-teacher-text-secondary">
        Showing {teachers.length} of {total} teachers
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
          {error}
        </div>
      )}

      {/* Teachers Table */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teacher-bg border-b border-teacher-border">
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">
                  Institute
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">
                  Subjects
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">
                  Batches
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-teacher-text-secondary">
                    Loading teachers...
                  </td>
                </tr>
              ) : teachers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-teacher-text-secondary">
                    No teachers found
                  </td>
                </tr>
              ) : (
                teachers.map((teacher) => (
                  <tr key={teacher.id} className="hover:bg-teacher-bg">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-teacher-text-primary">
                        {teacher.first_name} {teacher.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-teacher-text-secondary">{teacher.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-teacher-text-primary">{teacher.coaching_institute_name || '-'}</div>
                      <div className="text-xs text-teacher-text-secondary">{teacher.coaching_institute_location || ''}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(teacher.subjects || []).map(s => (
                          <span key={s} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
                            {s === 'physics' ? 'Phy' : s === 'chemistry' ? 'Chem' : 'Maths'}
                          </span>
                        ))}
                        {(!teacher.subjects || teacher.subjects.length === 0) && (
                          <span className="text-xs text-teacher-text-tertiary">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-primary-600">
                        {(teacher.batch_ids || []).length}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge isActive={teacher.is_active} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-teacher-text-secondary">
                      {formatDate(teacher.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/teachers/${teacher.id}`}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
